import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, usePopover } from "../ui";
import type { Task } from "../core/schema";

/** A small combobox to pick another task by title (dependencies, relations, sequences). */
export function TaskPicker({
  tasks,
  exclude,
  onPick,
  placeholder,
  testId,
  compact,
}: {
  tasks: Task[];
  exclude: Set<string>;
  onPick(id: string): void;
  placeholder: string;
  testId?: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const { open, setOpen, ref } = usePopover<HTMLDivElement>();
  const hits = useMemo(() => {
    const n = q.trim().toLowerCase();
    return tasks
      .filter((x) => !exclude.has(x.id) && x.archivedAt === null && (!n || x.title.toLowerCase().includes(n)))
      .slice(0, 8);
  }, [tasks, exclude, q]);
  return (
    <div ref={ref} className="relative">
      <Input
        compact={compact}
        value={q}
        placeholder={placeholder}
        aria-label={placeholder}
        data-testid={testId}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && hits[0]) {
            e.preventDefault();
            onPick(hits[0].id);
            setQ("");
            setOpen(false);
          }
        }}
      />
      {open && hits.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-auto rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700"
        >
          {hits.map((x) => (
            <li key={x.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full truncate rounded-sm px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => {
                  onPick(x.id);
                  setQ("");
                  setOpen(false);
                }}
              >
                {x.title || t("task.untitled")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
